import React, { useMemo, useState, useEffect } from 'react';
import { EMPHASIS, FlexColumn, StepCard, Text } from '@forecast-it/design-system';
import CustomScrollDiv from '../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import Styled from 'styled-components';
import { projectUrl } from '../../directApi';
import { MODAL_TYPE, showModal } from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import { PhaseCard } from '../baseline/PhaseDeliverablesCard';
import { LoadMore } from '../../components/loaders/LoadMore';
import FinancialCalculationTrigger, { financialCalculationTriggerQuery, } from '../../components/new-ui/project/project-budget-v3/loaders/FinancialCalculationTrigger';
import { createProjectUpdateToast } from '../../components/new-ui/project/project-budget-v3/util/BudgetUtils';
import { subscribe, EVENT_ID, unsubscribe } from '../../containers/event_manager';
import { getInitialBaselineItems } from '../baseline/Util';
const PageWrapper = Styled.div `
	padding: 0 32px;
`;
const DescriptionContainer = Styled.div `
	width: 485px;
`;
const StepCardsContainer = Styled.div `
	display: flex;
	flex-direction: row;
	align-items: stretch;
	box-sizing: border-box;
	gap: 12px;
	margin: 24px 0 10px;
`;
const placeholderContextMenuOptions = [
    {
        name: 'option 1',
        onClick: () => {
            return;
        },
    },
    {
        name: 'option 2',
        onClick: () => {
            return;
        },
    },
    {
        name: 'option 3',
        onClick: () => {
            return;
        },
    },
];
const DeliverablesGuideSection = ({ project, company, pageRetry, hasBaselineItems }) => {
    var _a, _b, _c;
    const intl = useIntl();
    const history = useHistory();
    const { formatMessage } = intl;
    const currency = ((project === null || project === void 0 ? void 0 : project.rateCard) ? project === null || project === void 0 ? void 0 : project.rateCard.currency : company === null || company === void 0 ? void 0 : company.currency) || '';
    const navigateToBaselineEdit = () => {
        history.push(`${projectUrl(project === null || project === void 0 ? void 0 : project.companyProjectId, project === null || project === void 0 ? void 0 : project.customProjectId)}/baseline/edit`);
    };
    const phases = (_a = project === null || project === void 0 ? void 0 : project.phases) === null || _a === void 0 ? void 0 : _a.edges;
    const deliverables = (_b = project === null || project === void 0 ? void 0 : project.deliverables) === null || _b === void 0 ? void 0 : _b.edges;
    const isPhasesCompleted = useMemo(() => phases.length > 0, [phases.length]);
    const isDeliverablesCompleted = useMemo(() => deliverables.length > 0, [deliverables.length]);
    const [retryCallback, setRetryCallback] = useState();
    const [retryProcessing, setRetryProcessing] = useState(false);
    const [retryProps, setRetryProps] = useState();
    const [financialCalculationTriggerTimestamp, setFinancialCalculationTriggerTimestamp] = useState();
    const baselineItems = getInitialBaselineItems(((_c = project === null || project === void 0 ? void 0 : project.phases) === null || _c === void 0 ? void 0 : _c.edges) || []);
    const isHoursAndExpensesDisabled = useMemo(() => !isPhasesCompleted || !isDeliverablesCompleted, [isPhasesCompleted, isDeliverablesCompleted]);
    const updateProject = props => {
        if (retryCallback) {
            retryCallback();
            setRetryProps(props);
            setRetryProcessing(true);
        }
    };
    const handleRetryCompletion = result => {
        if (!result.isSmallProject && !(retryProps === null || retryProps === void 0 ? void 0 : retryProps.noToast)) {
            createProjectUpdateToast(intl, result.duration);
        }
    };
    function handleFinancialCalculationTriggerResponse(retry, result) {
        if (!retryCallback) {
            setRetryCallback(() => retry);
        }
        if (retryProcessing && result.timestamp !== financialCalculationTriggerTimestamp) {
            handleRetryCompletion(result);
            setRetryProcessing(false);
        }
    }
    const showGuide = !hasBaselineItems;
    useEffect(() => {
        if (sessionStorage.getItem('baselineProjectUpdate') === 'true') {
            sessionStorage.removeItem('baselineProjectUpdate');
            setRetryProcessing(true);
        }
        subscribe(EVENT_ID.BUDGET_UPDATE_PROJECT, updateProject);
        return () => {
            unsubscribe(EVENT_ID.BUDGET_UPDATE_PROJECT, updateProject);
        };
    });
    return (React.createElement(LoadMore, { key: "financial-calculation-trigger", query: financialCalculationTriggerQuery, variables: {
            projectId: project.id,
        }, loader: React.createElement(FinancialCalculationTrigger, null) }, (result, financialTriggerRetry) => {
        setFinancialCalculationTriggerTimestamp(result.timestamp);
        handleFinancialCalculationTriggerResponse(financialTriggerRetry, result);
        if (!financialCalculationTriggerTimestamp || financialCalculationTriggerTimestamp !== result.timestamp) {
            pageRetry();
        }
        return (React.createElement(CustomScrollDiv, { horizontalcontent: true },
            React.createElement(PageWrapper, { "data-cy": 'baseline-deliverables-guide' },
                showGuide && (React.createElement("div", null,
                    React.createElement(DescriptionContainer, null,
                        React.createElement(Text, { size: '2' },
                            React.createElement(FormattedMessage, { id: 'deliverables.guide_section.description' }))),
                    React.createElement(StepCardsContainer, null,
                        React.createElement(StepCard, { completed: isPhasesCompleted, headerTitle: isPhasesCompleted
                                ? formatMessage({ id: 'common.done' })
                                : formatMessage({ id: 'common.todo' }), headerIcon: isPhasesCompleted ? 'checkCircle' : undefined, title: formatMessage({ id: 'deliverables.guide_section.phases.title' }), icon: 'phase', button: {
                                label: formatMessage({ id: 'deliverables.guide_section.phases.button' }),
                                emphasis: isPhasesCompleted ? EMPHASIS.MEDIUM : EMPHASIS.HIGH,
                                onClick: () => showModal({
                                    type: MODAL_TYPE.PHASE_MANAGEMENT_MODAL,
                                    initialPhases: phases,
                                    project,
                                }),
                            } },
                            React.createElement(FormattedMessage, { id: 'deliverables.guide_section.phases.description' })),
                        React.createElement(StepCard, { completed: isDeliverablesCompleted, headerTitle: isDeliverablesCompleted
                                ? formatMessage({ id: 'common.done' })
                                : formatMessage({ id: 'common.todo' }), headerIcon: isDeliverablesCompleted ? 'checkCircle' : undefined, title: formatMessage({ id: 'deliverables.guide_section.deliverables.title' }), icon: 'deliverable', button: {
                                label: formatMessage({ id: 'deliverables.guide_section.deliverables.button' }),
                                emphasis: isDeliverablesCompleted ? EMPHASIS.MEDIUM : EMPHASIS.HIGH,
                                onClick: () => showModal({
                                    type: MODAL_TYPE.MANAGE_DELIVERABLES_MODAL,
                                    deliverables,
                                    projectId: project.id,
                                }),
                            } },
                            React.createElement(FormattedMessage, { id: 'deliverables.guide_section.deliverables.description' })),
                        React.createElement(StepCard, { completed: false, disabled: isHoursAndExpensesDisabled, headerTitle: isHoursAndExpensesDisabled
                                ? formatMessage({
                                    id: 'deliverables.guide_section.hours_and_expenses.header_title',
                                })
                                : formatMessage({ id: 'common.todo' }), headerIcon: isHoursAndExpensesDisabled ? 'lockClosed' : undefined, title: formatMessage({ id: 'deliverables.guide_section.hours_and_expenses.title' }), icon: 'timeEstimate', button: {
                                label: formatMessage({
                                    id: 'deliverables.guide_section.hours_and_expenses.button',
                                }),
                                emphasis: EMPHASIS.HIGH,
                                onClick: () => navigateToBaselineEdit(),
                            } },
                            React.createElement(FormattedMessage, { id: 'deliverables.guide_section.hours_and_expenses.description' }))))),
                React.createElement(FlexColumn, { gap: "m" }, phases === null || phases === void 0 ? void 0 : phases.map(phaseEdge => {
                    if (phaseEdge && phaseEdge.node && company) {
                        const phase = phaseEdge.node;
                        const items = baselineItems
                            .filter(item => item.phaseId === phase.id)
                            .map((item, index) => (Object.assign(Object.assign({}, item), { index })));
                        return (React.createElement(PhaseCard, { phase: phase, currency: currency, contextMenuOptions: placeholderContextMenuOptions, company: company, baselineItems: items }));
                    }
                    return null;
                }).filter(Boolean)))));
    }));
};
export default DeliverablesGuideSection;
